import Component from "@glimmer/component";
import { task, all } from "ember-concurrency";
import { gt, not } from "ember-truth-helpers";
import { on } from "@ember/modifier";
import { fn } from "@ember/helper";
import { t } from "ember-intl";
import { tracked } from "@glimmer/tracking";
import { service } from "@ember/service";
import PageFooter from "glesys-controlpanel/components/page-footer";
import Cost from "glesys-controlpanel/components/cost";
import canManage from "glesys-controlpanel/helpers/can-manage";
import GButton from "glesys-controlpanel/components/g-button";
import TaskForm from "glesys-controlpanel/components/task-form";
import Label from "glesys-controlpanel/components/label";
import GInput from "glesys-controlpanel/components/g-input";
import Select, { Option } from "glesys-controlpanel/components/select";
import RadioCheckboxTile from "glesys-controlpanel/components/radio-checkbox-tile";
import InfoPopup from "glesys-controlpanel/components/info-popup";
import DataLoader from "glesys-controlpanel/components/data-loader";
import DataCenter from "glesys-controlpanel/components/data-center";
import IconsPlus from "glesys-controlpanel/components/icons/plus";
import { saveRecord } from "@warp-drive/legacy/compat/builders";
import PageContent from "glesys-controlpanel/components/page-content";
import Box from "glesys-controlpanel/components/box";
import PageContentServiceIntro from "glesys-controlpanel/components/page-content/service-intro";

export default class RemoteHandsRequestCreate extends Component {
  @service cost;
  @service store;
  @service router;

  @tracked selectedServices = [];
  @tracked selectedExecutionOption = "asap";
  @tracked selectedShipments = [];
  @tracked instructions;
  @tracked title;
  @tracked trackingNumbers = [""];

  get project() {
    return this.args.model.project;
  }

  get remoteHandsCost() {
    return this.cost.forRemoteHands().officeHours;
  }

  addTrackingNumber = () => {
    this.trackingNumbers = [...this.trackingNumbers, ""];
  };

  removeTrackingNumber = (index) => {
    this.trackingNumbers = this.trackingNumbers.filter((_, i) => i !== index);
  };

  onFormChange = ({ title, services, executionOption, instructions, trackingNumbers, existingShipments }) => {
    this.selectedServices = [].concat(services).filter(Boolean);
    this.selectedExecutionOption = executionOption;
    this.instructions = instructions;
    this.title = title;
    this.trackingNumbers = [].concat(trackingNumbers ?? []);
    this.selectedShipments = [].concat(existingShipments).filter(Boolean);
  };

  loadServicesTask = task(async () => {
    let serversPromise = this.project.get("servers");
    let racksPromise = this.project.get("colocationRacks");

    let [servers, racks] = await all([serversPromise, racksPromise]);

    return servers.filter((server) => server.isColocation).concat(racks);
  });

  loadShipmentsTask = task(async () => {
    let shipments = await this.project.get("shipments");

    return shipments.filter(
      (shipment) => !shipment.remoteHandsRequest && !shipment.isCanceled && !shipment.isDelivered,
    );
  });

  createRemoteHandsRequestTask = task(async () => {
    let trackingIds = this.trackingNumbers.map((trackingNumber) => trackingNumber.trim()).filter(Boolean);

    let remoteHandsRequest = this.store.createRecord("remote-hands-request", {
      project: this.project,
      title: this.title.trim(),
      servicekeys: this.selectedServices,
      executionoption: this.selectedExecutionOption,
      instructions: this.instructions,
      trackingids: trackingIds.length ? trackingIds : null,
      shipmentkeys: this.selectedShipments.length ? this.selectedShipments : null,
    });

    try {
      await this.store.request(saveRecord(remoteHandsRequest));
    } catch (error) {
      remoteHandsRequest.unloadRecord();

      throw error;
    }

    if (trackingIds.length) {
      await this.project
        .hasMany("shipments")
        .reload()
        .catch(() => {});
    }

    this.router.transitionTo("remote-hands-request.details", remoteHandsRequest.id);
  });

  get canSubmit() {
    return this.selectedServices.length > 0 && this.instructions?.trim().length > 0;
  }

  <template>
    <PageContent>
      <PageContentServiceIntro @title={{t "remote-hands-requests.create.title"}} />
      <TaskForm
        id="create-remote-hands-request-form"
        @onChange={{this.onFormChange}}
        @task={{this.createRemoteHandsRequestTask}}
      >
        <Box class="mb-6">
          <:header>{{t "label.title"}}</:header>
          <:default>
            <Label class="sr-only" for="title">{{t "label.title"}}</Label>
            <GInput
              class="mb-0"
              id="title"
              data-test-create-remote-hands-request-title
              name="title"
              value={{this.title}}
              placeholder={{t "remote-hands-requests.create.title-placeholder"}}
              maxlength="100"
            />
          </:default>
        </Box>
        <Box class="mb-6">
          <:header>{{t "label.services"}}</:header>
          <:default>
            <p class="mb-4 text-sm text-gray-500">
              {{t "remote-hands-requests.create.services-description"}}
            </p>
            <DataLoader @load={{this.loadServicesTask}} as |services|>
              {{#if services}}
                <Label class="sr-only" for="services">{{t "label.services"}}</Label>
                <Select
                  class="[appearance:base-select] p-0 h-auto"
                  id="services"
                  data-test-create-remote-hands-request-services
                  name="services"
                  @value={{this.selectedServices}}
                  multiple
                >
                  {{#each services as |service|}}
                    <Option
                      class="justify-between gap-4 px-4 py-3 border-b last:border-b-0 border-gray-200 even:bg-gray-50 odd:bg-white checked:bg-indigo-100 checked:hover:bg-indigo-100 checked:bg-none! checked:[&_.text-gray-500]:text-gray-600 [&::checkmark]:hidden"
                      value={{service.id}}
                    >
                      {{#if service.cabinet}}
                        <span>
                          {{service.cabinet}}
                          <span class="ml-1 text-gray-500">{{service.id}}</span>
                        </span>
                        <span class="text-gray-500 lowercase">{{service.site}}</span>
                      {{else}}
                        <span>{{service.id}}</span>
                        <span class="text-gray-500">
                          {{t "remote-hands-requests.create.colocation-server"}}
                        </span>
                      {{/if}}
                    </Option>
                  {{/each}}
                </Select>
                <p class="mt-2 text-xs text-gray-500 supports-[appearance:base-select]:hidden">
                  {{t "remote-hands-requests.create.services-hint"}}
                </p>
              {{else}}
                <p class="text-sm text-black" data-test-create-remote-hands-request-no-services>
                  {{t "remote-hands-requests.create.no-services"}}
                </p>
              {{/if}}
            </DataLoader>
          </:default>
        </Box>

        <Box class="mb-6">
          <:header>{{t "label.execution-option"}}</:header>
          <:default>
            <div class="grid grid-cols-1 @lg:grid-cols-2 gap-4">
              <RadioCheckboxTile
                data-test-create-remote-hands-request-execution-option="asap"
                @value="asap"
                @selected={{this.selectedExecutionOption}}
                @group="executionOption"
              >
                <div class="grid gap-1">
                  <div class="flex gap-2 items-center text-md">
                    {{t "remote-hands-requests.create.execution-option.asap.text"}}
                    <InfoPopup>
                      {{t "remote-hands-requests.create.execution-option.asap.info-popup"}}
                    </InfoPopup>
                  </div>
                  <p class="text-gray-500">
                    {{t "remote-hands-requests.create.execution-option.asap.sub-text"}}
                  </p>
                </div>
              </RadioCheckboxTile>
              <RadioCheckboxTile
                data-test-create-remote-hands-request-execution-option="scheduled"
                @value="scheduled"
                @selected={{this.selectedExecutionOption}}
                @group="executionOption"
              >
                <div class="grid gap-1">
                  <p class="text-md">
                    {{t "remote-hands-requests.create.execution-option.scheduled.text"}}
                  </p>
                  <p class="text-gray-500">
                    {{t "remote-hands-requests.create.execution-option.scheduled.sub-text"}}
                  </p>
                </div>
              </RadioCheckboxTile>
            </div>
          </:default>
        </Box>

        <Box class="mb-6">
          <:header>{{t "label.instructions"}}</:header>
          <:default>
            <Label class="sr-only" for="instructions">{{t "label.instructions"}}</Label>
            <textarea
              class="w-full border text-sm border-gray-200 focus:border-indigo-700 rounded-sm p-3"
              id="instructions"
              data-test-create-remote-hands-request-instructions
              name="instructions"
              value={{this.instructions}}
              placeholder={{t "remote-hands-requests.create.instructions-placeholder"}}
              rows="6"
              required
            />
          </:default>
        </Box>

        <Box class="mb-6">
          <:header>{{t "remote-hands-requests.create.shipments-title"}}</:header>
          <:default>
            <p class="mb-4 text-sm text-gray-500">
              {{t "remote-hands-requests.create.shipments-description"}}
            </p>
            <DataLoader class="contents" @load={{this.loadShipmentsTask}} as |shipments|>
              {{#if shipments}}
                <div class="mb-6">
                  <Label class="sr-only" for="existingShipments">{{t "label.existing-shipments-optional"}}</Label>
                  <Select
                    class="[appearance:base-select] p-0 h-auto"
                    id="existingShipments"
                    data-test-create-remote-hands-request-existing-shipments
                    name="existingShipments"
                    @value={{this.selectedShipments}}
                    multiple
                  >
                    {{#each shipments as |shipment|}}
                      <Option
                        class="justify-between gap-4 px-4 py-3 border-b last:border-b-0 border-gray-200 even:bg-gray-50 odd:bg-white checked:bg-indigo-100 checked:hover:bg-indigo-100 checked:bg-none! checked:[&_.text-gray-500]:text-gray-600 [&::checkmark]:hidden"
                        value={{shipment.id}}
                      >
                        <span>
                          {{#if shipment.trackingid}}
                            {{shipment.trackingid}}
                            <span class="ml-1 text-gray-500">{{shipment.id}}</span>
                          {{else}}
                            {{shipment.id}}
                          {{/if}}
                        </span>
                        <DataCenter @dataCenter={{shipment.datacenter}} />
                      </Option>
                    {{/each}}
                  </Select>
                  <p class="mt-2 text-xs text-gray-500 supports-[appearance:base-select]:hidden">
                    {{t "remote-hands-requests.create.shipments-hint"}}
                  </p>
                </div>
              {{else}}
                <p class="mb-6 text-sm text-black" data-test-create-remote-hands-request-no-shipments>
                  {{t "remote-hands-requests.create.no-shipments"}}
                </p>
              {{/if}}
            </DataLoader>
            <Label for="tracking-number-0">{{t "label.tracking-numbers-optional"}}</Label>
            <div class="grid gap-4">
              {{#each this.trackingNumbers key="@index" as |trackingNumber index|}}
                <div class="flex items-center gap-4">
                  <GInput
                    class="mb-0"
                    id="tracking-number-{{index}}"
                    aria-label={{t "label.tracking-id"}}
                    data-test-create-remote-hands-request-tracking-number={{index}}
                    name="trackingNumbers"
                    value={{trackingNumber}}
                  />
                  {{#if (gt this.trackingNumbers.length 1)}}
                    <button
                      class="text-sm text-gray-500 cursor-pointer"
                      aria-label={{t
                        "remote-hands-requests.create.remove-tracking-number-aria-label"
                        trackingNumber=trackingNumber
                      }}
                      data-test-create-remote-hands-request-remove-tracking-number={{index}}
                      type="button"
                      {{on "click" (fn this.removeTrackingNumber index)}}
                    >
                      {{t "button.remove"}}
                    </button>
                  {{/if}}
                </div>
              {{/each}}
            </div>
            <button
              class="mt-4 flex items-center gap-1 text-sm text-indigo-700 cursor-pointer"
              data-test-create-remote-hands-request-add-tracking-number
              type="button"
              {{on "click" this.addTrackingNumber}}
            >
              <IconsPlus class="size-4" />
              {{t "button.add-tracking-number"}}
            </button>
          </:default>
        </Box>
      </TaskForm>
    </PageContent>
    <PageFooter class="bg-white flex justify-end items-center gap-6 sticky bottom-0 w-full h-auto">
      <span class="flex items-center gap-2">
        <Cost @price={{this.remoteHandsCost.cost}} @currency={{this.remoteHandsCost.currency}} @interval="h" />
        <span class="text-gray-500 text-sm">
          {{t "remote-hands-requests.create.rate-billing"}}
        </span>
      </span>
      {{#if (canManage)}}
        <GButton
          data-test-create-remote-hands-request-submit
          form="create-remote-hands-request-form"
          @type="submit"
          @color="green"
          @disabled={{not this.canSubmit}}
          @loading={{this.createRemoteHandsRequestTask.isRunning}}
        >
          {{t "button.create"}}
        </GButton>
      {{/if}}
    </PageFooter>
  </template>
}
